import { selectPreserveAspectRatio, selectMirrorVideo } from 'features/streaming/streamingSlice';
import { selectVideoFacingMode } from 'features/user-media/userMediaSlice';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { usePublishingFeed } from 'hooks/usePublishingFeed';
import { Video } from 'features/streaming/components/media/Video';
import { LOCAL_VIDEO_PIP_ID } from 'utils/pip/video-pip/VideoPipRenderer';
import { useAppSelector } from 'store/hooks';

interface LocalStreamProps {
  mode: 'media' | 'screenshare';
  id: string;
}

const LocalVideoStream = ({ mode, id }: LocalStreamProps) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const { control } = usePublishingFeed(mode);
  const preserveAspectRatio = useAppSelector(selectPreserveAspectRatio);
  const mirrorVideo = useAppSelector(selectMirrorVideo);

  const mediaAspectRatio = preserveAspectRatio ? 'fit' : 'crop';

  const [aspectMode, setAspectMode] = useState(mode === 'screenshare' ? 'fit' : mediaAspectRatio);

  const facingMode = useAppSelector(selectVideoFacingMode);

  const shouldMirror = mode === 'media' && mirrorVideo && facingMode !== 'environment';

  useEffect(() => {
    control.mediaController.attachToElement(id, ref);
  }, [ref, control, id]);

  useEffect(() => {
    if (preserveAspectRatio && mode === 'media') {
      setAspectMode('fit');
    }
  }, [preserveAspectRatio, mode]);

  const handleResize = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (mode === 'screenshare') {
      return;
    }

    if (preserveAspectRatio) {
      return;
    }

    const video = e.target as HTMLVideoElement;

    const newAspectMode = video.videoWidth > video.videoHeight! ? 'crop' : 'fit';

    if (newAspectMode !== aspectMode) {
      setAspectMode(newAspectMode);
    }
  };

  return (
    <Video
      className={`fitment-${aspectMode}`}
      muted
      playsInline
      mirror={shouldMirror}
      ref={ref}
      onTimeUpdate={handleResize}
      id={mode === 'media' ? LOCAL_VIDEO_PIP_ID : undefined}
    />
  );
};

export default LocalVideoStream;
